import React, { useState } from 'react';
import { apiPublic } from '../lib/api';
import { CheckCircle2 } from 'lucide-react';

const initialState = {
    contact_name: '', email: '', phone: '',
    company_name: '', vat_number: '',
    num_persons: 2,
    billing_street: '', billing_postcode: '', billing_city: '', billing_country: 'België',
    notes: '',
    terms_accepted: false,
    hp_field: '',
};

const VipRequestForm = ({ settings, onClose }) => {
    const [form, setForm] = useState(initialState);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.contact_name.trim()) e.contact_name = 'Verplicht';
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Geldig e-mailadres verplicht';
        if (!form.phone.trim() || form.phone.trim().length < 5) e.phone = 'Telefoonnummer verplicht';
        if (!form.billing_street.trim()) e.billing_street = 'Verplicht';
        if (!form.billing_postcode.trim()) e.billing_postcode = 'Verplicht';
        if (!form.billing_city.trim()) e.billing_city = 'Verplicht';
        if (!form.billing_country.trim()) e.billing_country = 'Verplicht';
        if (!form.terms_accepted) e.terms_accepted = 'Akkoord vereist';
        if (!Number.isInteger(Number(form.num_persons)) || form.num_persons < 1 || form.num_persons > 20)
            e.num_persons = '1 t/m 20';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await apiPublic.post('/vip-requests', { ...form, num_persons: Number(form.num_persons) });
            setSubmitted(true);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Aanvraag mislukt. Probeer later opnieuw.';
            setErrors({ _form: typeof detail === 'string' ? detail : 'Validatie fout. Controleer je gegevens.' });
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="text-center py-8" data-testid="vip-form-success">
                <CheckCircle2 size={56} className="mx-auto text-[var(--green)] mb-4" aria-hidden="true" />
                <h4 className="font-display text-xl text-[var(--green-dark)] mb-3">Bedankt voor je aanvraag!</h4>
                <p className="text-sm text-[var(--green-dark)] max-w-md mx-auto leading-relaxed">
                    {settings?.vip_thanks_text || 'Bedankt voor je aanvraag. We nemen binnen 2 werkdagen contact met je op voor bevestiging en facturatie.'}
                </p>
                <button onClick={onClose} className="zz-btn zz-btn--primary mt-6" data-testid="vip-form-close-success">
                    Sluiten
                </button>
            </div>
        );
    }

    const unitPrice = Number(settings?.vip_unit_price) || 75;
    const total = unitPrice * Number(form.num_persons || 0);

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="vip-form" noValidate>
            <p className="text-xs text-[var(--green-dark)] mb-4">Velden met <span className="text-red-600">*</span> zijn verplicht.</p>

            <input type="text" name="hp_field" value={form.hp_field} onChange={(e) => set('hp_field', e.target.value)} style={{ display: 'none' }} tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <Field label="Contactpersoon *" error={errors.contact_name}>
                <input type="text" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className={inputCls(errors.contact_name)} required data-testid="vip-field-contact" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="E-mail *" error={errors.email}>
                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputCls(errors.email)} required data-testid="vip-field-email" />
                </Field>
                <Field label="Telefoon *" error={errors.phone}>
                    <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+32 ..." className={inputCls(errors.phone)} required data-testid="vip-field-phone" />
                </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Bedrijfsnaam">
                    <input type="text" value={form.company_name} onChange={(e) => set('company_name', e.target.value)} className={inputCls()} data-testid="vip-field-company" />
                </Field>
                <Field label="BTW-nummer">
                    <input type="text" value={form.vat_number} onChange={(e) => set('vat_number', e.target.value)} placeholder="BE0123.456.789" className={inputCls()} data-testid="vip-field-vat" />
                </Field>
            </div>

            <Field label="Aantal personen *" error={errors.num_persons}>
                <select value={form.num_persons} onChange={(e) => set('num_persons', Number(e.target.value))} className={inputCls(errors.num_persons)} required data-testid="vip-field-persons">
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>{n} {n === 1 ? 'persoon' : 'personen'}</option>
                    ))}
                </select>
            </Field>

            {settings?.vip_show_price_public && (
                <div className="bg-[var(--yellow)]/40 border border-[var(--green)] rounded-xl p-3 text-sm text-[var(--green-dark)]" data-testid="vip-form-total">
                    <strong>Geschat totaal:</strong> €{total.toFixed(2)} (excl. BTW)
                </div>
            )}

            <fieldset className="border border-[var(--green)]/30 rounded-xl p-4 space-y-3">
                <legend className="font-display text-xs text-[var(--green-dark)] px-2">Factuuradres</legend>
                <Field label="Straat + huisnummer *" error={errors.billing_street}>
                    <input type="text" value={form.billing_street} onChange={(e) => set('billing_street', e.target.value)} className={inputCls(errors.billing_street)} required data-testid="vip-field-street" />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Postcode *" error={errors.billing_postcode}>
                        <input type="text" value={form.billing_postcode} onChange={(e) => set('billing_postcode', e.target.value)} className={inputCls(errors.billing_postcode)} required data-testid="vip-field-postcode" />
                    </Field>
                    <Field label="Gemeente *" error={errors.billing_city} className="col-span-2">
                        <input type="text" value={form.billing_city} onChange={(e) => set('billing_city', e.target.value)} className={inputCls(errors.billing_city)} required data-testid="vip-field-city" />
                    </Field>
                </div>
                <Field label="Land *" error={errors.billing_country}>
                    <input type="text" value={form.billing_country} onChange={(e) => set('billing_country', e.target.value)} className={inputCls(errors.billing_country)} required data-testid="vip-field-country" />
                </Field>
            </fieldset>

            <Field label="Opmerkingen">
                <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} className={inputCls()} placeholder="Allergieën, voorkeuren, bijzonderheden…" data-testid="vip-field-notes" />
            </Field>

            <label className="flex items-start gap-2.5 text-xs text-[var(--green-dark)] cursor-pointer">
                <input type="checkbox" checked={form.terms_accepted} onChange={(e) => set('terms_accepted', e.target.checked)} className="mt-0.5" data-testid="vip-field-terms" />
                <span>
                    Ik ga akkoord met de <a href="#vip-terms" className="underline">voorwaarden van de Crazy VIP Ervaring</a> en de verwerking van mijn persoonsgegevens voor facturatie en communicatie. *
                    {errors.terms_accepted && <span className="block text-red-600 mt-1">{errors.terms_accepted}</span>}
                </span>
            </label>

            {errors._form && (
                <div className="bg-red-50 border border-red-300 rounded-md p-3 text-xs text-red-800" data-testid="vip-form-error">{errors._form}</div>
            )}

            <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700">
                    Annuleren
                </button>
                <button type="submit" disabled={submitting} className="zz-btn zz-btn--primary" data-testid="vip-form-submit">
                    {submitting ? 'Verzenden…' : 'Aanvraag verzenden'}
                </button>
            </div>
        </form>
    );
};

const inputCls = (err) => `w-full bg-white border ${err ? 'border-red-400' : 'border-[var(--green)]/40'} rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[var(--green)]`;

const Field = ({ label, error, children, className = '' }) => (
    <div className={className}>
        <label className="block text-xs font-semibold text-[var(--green-dark)] mb-1.5">{label}</label>
        {children}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
);

export default VipRequestForm;
